/**
 * @module common/components/pinch_view
 */
import React, { Component, PropTypes } from 'react';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import PinchZoomPan, { repositionImage } from 'common/components/pinch_zoom_pan';
import Icon from 'common/components/svg_icon';

/**
 * Outer component for image zooming that provides a layer for all the image controls.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
class PinchView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      x: 0,
      y: 0,
    };

    this.onZoomIn = this.onZoomIn.bind(this);
    this.onZoomOut = this.onZoomOut.bind(this);
    this.imagePositionChange = this.imagePositionChange.bind(this);
  }

  onZoomIn() {
    const { zoom, imageSize } = this.props;
    const elemWidth = ReactDOM.findDOMNode(this.image).offsetWidth;

    this.props.onZoomIn();
    this.imagePositionChange(repositionImage(this.state.x, zoom, imageSize, elemWidth, null));
  }

  onZoomOut() {
    const { zoom, imageSize } = this.props;
    const elemWidth = ReactDOM.findDOMNode(this.image).offsetWidth;

    this.props.onZoomOut();
    this.imagePositionChange(repositionImage(this.state.x, zoom, imageSize, elemWidth, null));
  }

  imagePositionChange(obj) {
    this.setState({
      x: obj.x,
      y: obj.y || 0,
    });
  }

  getHolderStyle() {
    return {
      position: 'relative',
      width: '100%',
      height: '100%',
    };
  }

  getContentStyle() {
    return {
      width: '100%',
      height: '100%',
      align: 'center',
      display: 'flex',
      overflow: 'hidden',
      overflowY: 'auto',
      justifyContent: 'center',
    };
  }

  getWrapperStyle(obj) {
    return {
      transform: `scale(${obj.zoom}) translateY(${obj.y}px)translateX(${obj.x}px)`,
      transition: '.3s ease-out',
      transformOrigin: 'top',
      height: `${obj.imageSize.height}px`,
    };
  }

  renderDebug(obj) {
    return (
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        background: '#FFF',
        padding: '3px 6px',
      }}
      >
        Scale: { obj.zoom }, X: { obj.x }, Y: { obj.y }
      </div>
    );
  }

  render() {
    const { debug, maxScale, zoom, children, imageSize,
      onToggleFullscreen, showFullscreen } = this.props;
    const { x, y } = this.state;

    return (
      <PinchZoomPan
        maxScale={maxScale}
        imageSize={imageSize}
        zoom={zoom}
        imagePositions={{ x, y }}
        ref={(c) => { if (c) this.image = c; }}
        onPositionChange={this.imagePositionChange}
        render={(obj) => (
          <div style={this.getHolderStyle()}>
            <div style={this.getContentStyle()}>
              <div style={(this.getWrapperStyle(obj))}>
                {children}
              </div>
            </div>
            <div className="hide-for-small-only zoom-controls">
              <div className="zoom-buttons">
                {showFullscreen &&
                  <button type="button" className="btn btn-icon-only btn-fullscrean" onClick={onToggleFullscreen}>
                    <Icon className={classNames('svg-icon', 'svg-icon-lg')} icon="fullscreen"/>
                  </button>
                }
                <button type="button" className="btn btn-icon-only btn-zoomin" onClick={this.onZoomIn}>
                  <Icon className={classNames('svg-icon', 'svg-icon-lg')} icon="zoomIn"/>
                </button>
                <button type="button" className="btn btn-icon-only btn-zoomout" onClick={this.onZoomOut}>
                  <Icon className={classNames('svg-icon', 'svg-icon-lg')} icon="zoomOut"/>
                </button>
              </div>
            </div>
            <div className="show-for-small-only zoom-message">
              <span>Pinch to zoom</span>
            </div>
            {debug && this.renderDebug(obj)}
          </div>
        )}
      />
    );
  }
}

/**
 * PropTypes.
 *
 * @property {object} imageSize - object with width and height of the displayed image
 * @property {number} maxScale - float number defining the max zoom limit. We assume the min zoom value is
 *                                1 / half of zooming levels, so in case of 8 zooming levels it's 0.25.
 * @property {number} zoom - current image zoom
 * @property {bool} debug - variable that controls debug mode on/off
 * @property {node} children - image and everything else that needs to be rendered in the PinchView
 * @property {func} onZoomIn - function called when image is zoomed-in
 * @property {func} onZoomOut - function called wehn image is zoomed-out
 * @property {func} onToggleFullscreen - function called wehn full screen is requested
 */
PinchView.propTypes = {
  imageSize: PropTypes.object,
  maxScale: PropTypes.number,
  zoom: PropTypes.number,
  debug: PropTypes.bool,
  children: PropTypes.node,
  onZoomIn: PropTypes.func,
  onZoomOut: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
  showFullscreen: PropTypes.bool,
};

export default PinchView;



// WEBPACK FOOTER //
// ./common/components/pinch_view.js